import React, { useState } from 'react';
import {
  SuggestedAddress,
  QuickAddressSearchParams,
  VerifyLevel,
} from '../../__generated__/pge-types';
import useQas, { QasResult } from '../../hooks/useQas';
import {
  List,
  ListItem,
  Theme,
  makeStyles,
  createStyles,
  Typography,
  Grid,
} from '@material-ui/core';
import useWrapWithLoader from '../../hooks/useWrapWithLoading';
import { useTranslation } from '../../hooks/useTranslation';
import PGEButton from '../buttons';
import AlphaNumericTextField from '../alpha-numeric-text-field';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listButton: {
      color: theme.palette.primary.main,
    },
    inputArea: {
      '& .MuiTextField-root': {
        margin: 0,
      },
    },
    button: {
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        height: '57px',
      },
    },
  }),
);

type Props = {
  results: QasResult;
  onChange: (address: SuggestedAddress) => void;
  onUseEntered: () => void;
  onEdit: () => void;
  onSearch: (params: QuickAddressSearchParams, results: QasResult) => void;
  address: QuickAddressSearchParams;
};

export default function QasPicker({
  address,
  results,
  onChange,
  onSearch,
}: Props) {
  const classes = useStyles();
  const qas = useQas();
  const { wrapWithLoader } = useWrapWithLoader();
  const { t } = useTranslation();
  const [numberValue, setNumberValue] = useState('');
  const [showingList, setShowingList] = useState(false);
  const [error, setError] = useState<null | string>(null);

  if (results.type === 'NONE') {
    return null;
  }

  if (results.type === 'SINGLE' || results.type === 'AUTO') {
    const suggested = results.address;
    return (
      <Grid container direction="column" spacing={2}>
        <Grid item>
          <Typography variant="body1">{t('WE_RECOMMEND')}:</Typography>
        </Grid>
        <Grid item>
          <Typography variant="body1">
            <strong>
              {suggested.addressLine1}
              <br />
              {suggested.city} {suggested.state} {suggested.postal}
            </strong>
          </Typography>
        </Grid>

        <Grid item>
          <PGEButton onClick={() => onChange(suggested)}>
            {t('USE_RECOMMENDED_ADDRESS')}
          </PGEButton>
        </Grid>
      </Grid>
    );
  }

  if (results.type === 'MULTIPLE') {
    const list = (
      <List>
        {results.addresses.map(potential => {
          const [line1, line2] = potential.address!.split(', ');

          const addressDisplay = (
            <Typography>
              {line1}
              <br />
              {line2}
            </Typography>
          );

          return potential.isFullAddress && potential.moniker ? (
            <ListItem
              key={potential.address!}
              button
              className={classes.listButton}
              onClick={wrapWithLoader(async () => {
                onChange(await qas.searchById(potential.moniker!));
              })}
            >
              {addressDisplay}
            </ListItem>
          ) : (
            <ListItem key={potential.address!}>{addressDisplay}</ListItem>
          );
        })}
      </List>
    );

    if (results.verifyLevel === VerifyLevel.Multiple) {
      return (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body1">
              {t('OUR_SUGGESTED_MATCHES')}:
            </Typography>
          </Grid>
          <Grid item>{list}</Grid>
        </Grid>
      );
    } else {
      const message =
        results.verifyLevel === VerifyLevel.StreetPartial
          ? t('CONFIRM_HOUSE_NUMBER')
          : t('CONFIRM_PREMISE_NUMBER');

      const action =
        results.verifyLevel === VerifyLevel.StreetPartial
          ? (val: string, line1: string) => `${val} ${line1}`
          : (val: string, line1: string) => `${line1} # ${val}`;

      return (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <Typography variant="body1">{message}:</Typography>
          </Grid>

          <Grid item>
            <Grid container direction="row" spacing={1} alignItems="flex-start">
              <Grid item xs={6} className={classes.inputArea}>
                <AlphaNumericTextField
                  inputProps={{ minLength: 1, maxLength: 8 }}
                  value={numberValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (error && Boolean(e.target.value)) {
                      setError(null);
                    }
                    setNumberValue(e.target.value);
                  }}
                  onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                    Boolean(e.target.value)
                      ? setError(null)
                      : setError(t('NO_VALUE'))
                  }
                  error={Boolean(error)}
                  helperText={error}
                />
              </Grid>
              <Grid item xs={6}>
                <PGEButton
                  className={classes.button}
                  onClick={wrapWithLoader(async () => {
                    if (numberValue === '') {
                      setError(t('NO_VALUE'));
                    } else {
                      const newParams = {
                        ...address,
                        addressLine1: action(
                          numberValue,
                          address.addressLine1!,
                        ),
                      };

                      const newResults = await qas.search(newParams);
                      const notFoundMessage =
                        results.verifyLevel === VerifyLevel.StreetPartial
                          ? t('HOUSE_BUILDING_NOT_FOUND')
                          : t('APARTMENT_UNIT_NOT_FOUND');
                      if (newResults.type === 'NONE') {
                        setError(notFoundMessage);
                      } else {
                        const stillPartial =
                          newResults.type === results.type &&
                          results.verifyLevel === newResults.verifyLevel;
                        setError(stillPartial ? notFoundMessage : null);
                        setNumberValue('');
                        setShowingList(false);
                        onSearch(
                          newResults.type === results.type &&
                            results.verifyLevel === newResults.verifyLevel
                            ? address
                            : newParams,
                          newResults,
                        );
                      }
                    }
                  })}
                >
                  {t('CONFIRM_NUMBER')}
                </PGEButton>
              </Grid>
            </Grid>
          </Grid>

          <Grid item>
            <Grid container direction="column" spacing={1}>
              <Grid item>
                <Typography variant="body1">
                  <a
                    className="onClickLink"
                    onClick={() => setShowingList(!showingList)}
                  >
                    {showingList
                      ? t('HIDE_ALL_MATCHES')
                      : t('SHOW_ALL_MATCHES')}
                  </a>
                </Typography>
              </Grid>
              {showingList && <Grid item>{list}</Grid>}
            </Grid>
          </Grid>
        </Grid>
      );
    }
  }

  return null;
}
